import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_ICONS } from "../../../assets";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import OrderTable from "../../../components/Tables/OrderTable";
import TextInput from "../../../components/TextInputs";
import {
  setSaleCurrentPage,
  setSalePageLimit,
  setSaleTotalCount,
  setSaleTotalPages,
} from "../../../lib/slices/pagination/salePageSlice";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import {
  exportAllOrders,
  getAllOrders,
} from "../../../services/orderApiFn";
import {
  calculateProductOrderVatAmount,
  calculateSupplierTotalOrderPrice,
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../utils";
import useDebounce from "./../../../hooks/useDebounce";
import { CSVLink } from "react-csv";
import moment from "moment";
import { CURRENCY } from "../../../constants/Variables";

const headerData = [
  "createdAt",
  "orderId  ",
  "name",
  "subTotal",
  "platformFee",
  "shippingFee",
  "orderVatAmount",
  "vat",
  "totalBill",
  "paymentType",
  "deliveryType",
  "actions",
  // "invoice",
];

const tableRows = [
  "created_date",
  "order_id",
  "user_detail",
  "order_supplier_price",
  "order_platform_fee",
  "shipping_fee",
  "order_vat_amount",
  "order_tax",
  "order_total_bill",
  "order_payment_type",
  "order_delivery_type",
  "order_detail",
  // "download",
];

function AdminSales() {
  const csvLinkRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["sales", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const userData = useSelector((state) => state.userData.user);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const pagination = useSelector((state) => state.salePaginationReducer);
  const debouncedSearchTerm = useDebounce(searchKeys.ecommerceOrderId, 1000);
  const prevDebouncedSearchRef = useRef(debouncedSearchTerm);
  const fetchIdRef = useRef(0);

  const fetchAllOrders = async (orderId, page, limit) => {
    const query = {
      page,
      limit,
      orderId,
    };
    const fetchId = ++fetchIdRef.current;
    try {
      setIsLoading(true);
      const response = await getAllOrders(query);
      if (fetchId !== fetchIdRef.current) return;
      if (response?.data.status === "success") {
        const paginationData = response?.data?.data?.pagination;
        const data = response?.data?.data?.data;
        setAllOrders(data);
        dispatch(setSalePageLimit(paginationData.limit));
        dispatch(setSaleCurrentPage(paginationData.page));
        dispatch(setSaleTotalCount(paginationData.total));
        dispatch(setSaleTotalPages(paginationData.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      handleOpenSnackBar("error", error?.message);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!userData) return;

    const searchChanged =
      prevDebouncedSearchRef.current !== debouncedSearchTerm;

    if (searchChanged && pagination.page !== 1) {
      prevDebouncedSearchRef.current = debouncedSearchTerm;
      dispatch(setSaleCurrentPage(1));
      return;
    }

    prevDebouncedSearchRef.current = debouncedSearchTerm;
    fetchAllOrders(debouncedSearchTerm, pagination.page, pagination.limit);
  }, [userData, debouncedSearchTerm, pagination.page, pagination.limit]);

  const handlePageChange = (event, newPage) => {
    dispatch(setSaleCurrentPage(newPage + 1));
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setSalePageLimit(limit));
    dispatch(setSaleCurrentPage(1));
  };

  const handleViewDetail = (row) => {
    navigate(`/sale/details/${row?._id}`);
  };

  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const response = await exportAllOrders();
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];
        data.forEach((item) => {
          const discountAmount = item?.discount?.amount || 0;
          const discountVat = calculateVat(discountAmount, item?.vat);

          const orderSubTotal =
            calculateSupplierTotalOrderPrice(item?.order) - discountAmount;

          const totalVatAmount = roundTo2Decimal(
            calculateProductOrderVatAmount(item?.order) - discountVat,
          );

          const finalOrderTotal = roundTo2Decimal(
            orderSubTotal +
              item?.platformFee +
              item?.shippingFee +
              totalVatAmount,
          );

          formattedData.push({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Order Id": item.orderId || "-",
            "User Name":
              `${item.user?.firstname} ${item.user?.lastname}` || "-",
            "User Email": item.user?.email || "-",
            "User Contact": item.user?.contact || "-",
            "Sub Total": `${orderSubTotal} ${CURRENCY}` || "-",
            "Platform Fee": `${item.platformFee} ${CURRENCY}` || "-",
            "Shipping Fee": `${item.shippingFee} ${CURRENCY}` || "-",
            "Order VAT Amount": `${totalVatAmount} ${CURRENCY}` || "-",
            "VAT": `${item.vat}%` || "-",
            "Total Bill": `${finalOrderTotal} ${CURRENCY}` || "-",
            "Payment Type": item.paymentMethod || "-",
            "Delivery Type": item.deliveryType || "-",
          });
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"all_orders.csv"}
          className="csv-link"
          target="_blank"
          style={{ display: "none" }}
        />
        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          disabled={exportLoading}
          handleClickBtn={prepareExportData}
        />
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.ecommerceOrderId && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      ecommerceOrderId: "",
                    }),
                  );
                  dispatch(setSaleCurrentPage(1));
                }}
              />
            )
          }
          placeholder={t("searchByOrderId", { ns: "common" })}
          value={searchKeys.ecommerceOrderId}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                ecommerceOrderId: e.target.value,
              }),
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <>
      <PaperContainer
        title={t("saleList")}
        subtitle={t("viewAndManageSalesOrders")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        <OrderTable
          minWidth={1500}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allOrders}
          handleViewDetail={handleViewDetail}
          showDownloadBtn={true}
          isLoading={isLoading}
          paginatedData={pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </PaperContainer>
    </>
  );
}

export default AdminSales;
